import React, { createContext, useContext, useEffect, useState } from 'react';
import gsap from 'gsap';

const ModeContext = createContext();

export const useModeContext = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useModeContext must be used within a ModeProvider');
  }
  return context;
};

export const ModeProvider = ({ children }) => {
  const [activeMode, setActiveMode] = useState(() => {
    // Check localStorage for saved mode preference
    if (typeof window !== 'undefined') {
      const savedMode = localStorage.getItem('tomweb-mode');
      return savedMode ? parseInt(savedMode, 10) : 0;
    }
    return 0; // Default to Behaviour Change
  });
  
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Professional mode transition with subtle feedback
  const smoothSetActiveMode = (newMode) => {
    if (newMode === activeMode || isTransitioning) return;

    setIsTransitioning(true);
    
    // Simple, professional transition overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: ${newMode === 1 ? 'rgba(3, 3, 3, 0.95)' : 'rgba(248, 250, 252, 0.95)'};
      z-index: 9998;
      opacity: 0;
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    `;

    // Simple loading indicator
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      width: 32px;
      height: 32px;
      border: 2px solid ${newMode === 1 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(169, 187, 181, 0.2)'};
      border-top: 2px solid ${newMode === 1 ? '#F59E0B' : '#A9BBB5'};
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `;

    // Add CSS animation for spinner
    if (!document.getElementById('mode-transition-styles')) {
      const style = document.createElement('style');
      style.id = 'mode-transition-styles';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    overlay.appendChild(indicator);
    document.body.appendChild(overlay);

    // Smooth transition
    gsap.timeline()
      .to(overlay, {
        opacity: 1,
        duration: 0.2,
        ease: "power2.out"
      })
      .call(() => {
        // Change mode
        setActiveMode(newMode);
        localStorage.setItem('tomweb-mode', newMode.toString());
      }, [], 0.3)
      .to(overlay, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.inOut",
        delay: 0.2,
        onComplete: () => {
          document.body.removeChild(overlay);
          setIsTransitioning(false);
        }
      });
  };

  // Toggle global dark mode class based on activeMode
  useEffect(() => {
    const root = document.documentElement;
    if (activeMode === 1) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Save to localStorage whenever activeMode changes
    if (typeof window !== 'undefined') {
      localStorage.setItem('tomweb-mode', activeMode.toString());
    }
  }, [activeMode]);

  return (
    <ModeContext.Provider value={{ 
      activeMode, 
      setActiveMode: smoothSetActiveMode, 
      isTransitioning 
    }}>
      {children}
    </ModeContext.Provider>
  );
};
